<template>
    <section class="download-section">
        <div class="download-section__container">
            <div class="download-cards">
                <!-- Desktop downloads -->
                <div class="download-card">
                    <div class="download-card__icon-wrap">
                        <MonitorDesktop class="download-card__icon" />
                    </div>
                    <div class="download-card__items">
                        <div class="download-card__item">
                            <button class="download-btn" @click="downloadFile('windows')">
                                <Windows class="download-btn__logo" />
                                <span>{{ t('download.windows') }}</span>
                            </button>
                            <p class="download-card__desc">{{ t('download.windowsReq') }}</p>
                        </div>
                        <div class="download-card__item">
                            <button class="download-btn" @click="downloadFile('macos')">
                                <Apple class="download-btn__logo" />
                                <span>{{ t('download.macos') }}</span>
                            </button>
                            <p class="download-card__desc">{{ t('download.macosReq') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Mobile downloads -->
                <div class="download-card">
                    <div class="download-card__icon-wrap">
                        <Smartphone class="download-card__icon" />
                    </div>
                    <div class="download-card__items">
                        <div class="download-card__item">
                            <button class="download-btn" @click="downloadFile('android')">
                                <Android class="download-btn__logo" />
                                <span>{{ t('download.android') }}</span>
                            </button>
                            <p class="download-card__desc">{{ t('download.androidReq') }}</p>
                        </div>
                        <div class="download-card__item">
                            <button class="download-btn" @click="downloadFile('ios')">
                                <Apple class="download-btn__logo" />
                                <span>{{ t('download.ios') }}</span>
                            </button>
                            <p class="download-card__desc">{{ t('download.iosReq') }}</p>
                        </div>
                    </div>
                </div>

                <!-- QR code -->
                <div class="download-card download-card--qr">
                    <div class="download-card__icon-wrap">
                        <QrScan class="download-card__icon" />
                    </div>
                    <div class="qr-wrap">
                        <div class="qr-code">
                            <div class="qr-corner qr-corner--tl"></div>
                            <div class="qr-corner qr-corner--tr"></div>
                            <div class="qr-corner qr-corner--bl"></div>
                            <div class="qr-corner qr-corner--br"></div>
                            <canvas ref="canvas" class="qr-code__img" />
                        </div>
                        <p class="download-card__desc download-card__desc--qr">{{ t('download.qrTip') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { queryPortalConfigReq } from '@/api/menu'
import { useI18n } from 'vue-i18n'
import QrCode from 'qrcode'
import MonitorDesktop from '@/assets/icons/monitor-desktop.svg'
import Windows from '@/assets/icons/windows.svg'
import Apple from '@/assets/icons/apple.svg'
import Smartphone from '@/assets/icons/smartphone.svg'
import Android from '@/assets/icons/android.svg'
import QrScan from '@/assets/icons/qr-scan.svg'
import { checkInnerBrowser } from '@/utils/browser_env'


const windows = ref('');
const macos = ref('');
const android = ref('');
const ios = ref('');
const canvas = ref<HTMLCanvasElement>()


const { t } = useI18n()

function drawQRCode() {
    QrCode.toCanvas(canvas.value!, ios.value, { width: 134 })
}

async function queryConfig() {
    // downloadApp
    const res = await queryPortalConfigReq('downloadApp');
    if (res) {
        res.data.result.configList.forEach(item => {
            if (item.configKey === 'windows') {
                windows.value = item.configValue
            } else if (item.configKey === 'macos') {
                macos.value = item.configValue
            } else if (item.configKey === 'android') {
                android.value = item.configValue
            } else if (item.configKey === 'ios') {
                ios.value = item.configValue
            }
        })
        drawQRCode()
    }
}

function downloadFile(platform: 'windows' | 'macos' | 'android' | 'ios'): void {
    if (checkInnerBrowser()) {
        return
    }


    const urlMap: Record<string, string> = {
        windows: windows.value,
        macos: macos.value,
        android: android.value,
        ios: ios.value
    }
    const url = urlMap[platform]
    if (url && url !== '#') {
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}

onMounted(() => {
    queryConfig()
})

</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;
$card-bg: #eeeeef;
$icon-bg: #c6c6c8;
$icon-color: #fff;

.download-section {
    width: 100%;
    padding: 2.5rem 0;

    @media (min-width: $breakpoint-md) {
        padding: 4rem 0;
    }
}

.download-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.download-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    @media (min-width: $breakpoint-md) {
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
    }
}

.download-card {
    background: $card-bg;
    border-radius: 20px;
    padding: 1.75rem 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (min-width: $breakpoint-md) {
        padding: 2.5rem 2rem;
        border-radius: 24px;
    }

    &__icon-wrap {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: $icon-bg;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.25rem;
        flex-shrink: 0;

        @media (min-width: $breakpoint-md) {
            width: 72px;
            height: 72px;
            margin-bottom: 1.5rem;
        }
    }

    &__icon {
        width: 32px;
        height: 32px;
        color: $icon-color;

        @media (min-width: $breakpoint-md) {
            width: 36px;
            height: 36px;
        }
    }

    &__items {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.25rem;

        @media (min-width: $breakpoint-md) {
            gap: 1.5rem;
        }
    }

    &__item {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.625rem;
    }

    &__desc {
        font-size: 0.8125rem;
        color: $color-text-secondary;
        line-height: 1.5;
        margin: 0;
        text-align: center;

        @media (min-width: $breakpoint-md) {
            font-size: 0.9375rem;
        }

        &--qr {
            margin-top: 1rem;
        }
    }
}

.download-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.625rem;
    min-width: 180px;
    height: 46px;
    padding: 0 1.75rem;
    background: $orange;
    color: #fff;
    font-size: 0.9375rem;
    font-weight: 600;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s;
    white-space: nowrap;

    @media (min-width: $breakpoint-md) {
        height: 50px;
        font-size: 1rem;
        min-width: 200px;
    }

    &:hover {
        background: #e85d00;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    &__logo {
        width: 20px;
        height: 20px;
        flex-shrink: 0;

        @media (min-width: $breakpoint-md) {
            width: 22px;
            height: 22px;
        }
    }
}

/* QR Code card */
.download-card--qr {
    .qr-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }
}

.qr-code {
    position: relative;
    width: 150px;
    height: 150px;
    background: #fff;
    border-radius: 12px;
    padding: 8px;
    box-sizing: border-box;


    &__img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
    }
}

.qr-corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: $orange;
    border-style: solid;
    border-width: 0;

    @media (min-width: $breakpoint-md) {
        width: 24px;
        height: 24px;
    }

    &--tl {
        top: -4px;
        left: -4px;
        border-top-width: 3px;
        border-left-width: 3px;
        border-top-left-radius: 6px;
    }

    &--tr {
        top: -4px;
        right: -4px;
        border-top-width: 3px;
        border-right-width: 3px;
        border-top-right-radius: 6px;
    }

    &--bl {
        bottom: -4px;
        left: -4px;
        border-bottom-width: 3px;
        border-left-width: 3px;
        border-bottom-left-radius: 6px;
    }

    &--br {
        bottom: -4px;
        right: -4px;
        border-bottom-width: 3px;
        border-right-width: 3px;
        border-bottom-right-radius: 6px;
    }
}
</style>
